import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavHashLink as NavLink } from 'react-router-hash-link';
import { Menu, X } from 'lucide-react';

import './Navigation.css';
import { theme } from '../../theme/designSystem';
import { headerData } from '../../data/headerData';
import { useActiveSection } from '../../hooks/useActiveSection';
import { useScrollDirection } from '../../hooks/useScrollDirection';

const NAV_ITEMS = [
    { id: 'honors', label: 'Distinctions', href: '/#honors' },
    { id: 'projects', label: 'Work', href: '/#projects' },
    { id: 'experience', label: 'Experience', href: '/#experience' },
    { id: 'press', label: 'Press', href: '/#press' },
    { id: 'writing', label: 'Writing', href: '/#writing' },
    { id: 'contacts', label: 'Contact', href: '/#contacts' },
];

const SECTION_IDS = ['honors', 'projects', 'leadership', 'experience', 'press', 'writing', 'education', 'achievements', 'about', 'contacts'];


function Navigation() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { isScrolled } = useScrollDirection();
    const activeSection = useActiveSection(SECTION_IDS);
    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);
    const firstFocusableRef = useRef(null);
    const lastFocusableRef = useRef(null);

    // Close menu on ESC
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && mobileMenuOpen) {
                setMobileMenuOpen(false);
                menuButtonRef.current?.focus();
            }
        };

        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [mobileMenuOpen]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                mobileMenuOpen &&
                menuRef.current &&
                !menuRef.current.contains(e.target) &&
                !menuButtonRef.current?.contains(e.target)
            ) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuOpen]);

    // Lock body scroll when menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    // Focus trap
    const handleTabKey = useCallback((e) => {
        if (!mobileMenuOpen) return;

        if (e.key === 'Tab') {
            if (e.shiftKey) {
                if (document.activeElement === firstFocusableRef.current) {
                    e.preventDefault();
                    lastFocusableRef.current?.focus();
                }
            } else {
                if (document.activeElement === lastFocusableRef.current) {
                    e.preventDefault();
                    firstFocusableRef.current?.focus();
                }
            }
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, [handleTabKey]);

    // Focus first element when menu opens
    useEffect(() => {
        if (mobileMenuOpen) {
            firstFocusableRef.current?.focus();
        }
    }, [mobileMenuOpen]);

    const toggleMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMenu = () => {
        setMobileMenuOpen(false);
    };

    return (
        <>
            <nav
                className={`navigation ${isScrolled ? 'navigation--scrolled' : ''}`}
                style={{
                    backgroundColor: isScrolled ? 'rgba(13, 17, 23, 0.95)' : theme.secondary,
                }}
                role="navigation"
                aria-label="Main navigation"
            >
                <div className="navigation__container">
                    <NavLink to="/" className="navigation__logo" aria-label={`${headerData.name}, home`}>
                        <span className="navigation__mark" aria-hidden="true">RA</span>
                        <span className="navigation__wordmark" style={{ color: theme.primary }}>
                            Ravi Teja <span className="navigation__wordmark-last">Alchuri</span>
                        </span>
                    </NavLink>

                    <div className="navigation__desktop">
                        <ul className="navigation__links">
                            {NAV_ITEMS.map((item) => (
                                <li key={item.id}>
                                    <NavLink
                                        to={item.href}
                                        smooth
                                        className={`navigation__link ${activeSection === item.id ? 'navigation__link--active' : ''}`}
                                        style={{
                                            color: activeSection === item.id ? theme.accent : theme.textSecondary,
                                        }}
                                    >
                                        {item.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button
                        ref={menuButtonRef}
                        className="navigation__hamburger"
                        onClick={toggleMenu}
                        aria-expanded={mobileMenuOpen}
                        aria-controls="mobile-menu"
                        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        style={{ color: theme.primary }}
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </nav>

            <div
                className={`navigation__overlay ${mobileMenuOpen ? 'navigation__overlay--open' : ''}`}
                aria-hidden={!mobileMenuOpen}
            />

            <div
                ref={menuRef}
                id="mobile-menu"
                className={`navigation__mobile-panel ${mobileMenuOpen ? 'navigation__mobile-panel--open' : ''}`}
                style={{ backgroundColor: theme.tertiary }}
                role="dialog"
                aria-modal="true"
                aria-label="Mobile navigation menu"
            >
                <button
                    ref={firstFocusableRef}
                    className="navigation__close"
                    onClick={closeMenu}
                    aria-label="Close menu"
                    style={{ color: theme.primary }}
                >
                    <X size={24} />
                </button>

                <ul className="navigation__mobile-links">
                    {NAV_ITEMS.map((item) => (
                        <li key={item.id}>
                            <NavLink
                                to={item.href}
                                smooth
                                onClick={closeMenu}
                                className={`navigation__mobile-link ${activeSection === item.id ? 'navigation__mobile-link--active' : ''}`}
                                style={{
                                    color: activeSection === item.id ? theme.accent : theme.primary,
                                }}
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>

                <NavLink
                    ref={lastFocusableRef}
                    to="/#contacts"
                    smooth
                    onClick={closeMenu}
                    className="navigation__mobile-cta btn btn-primary"
                >
                    Get in Touch
                </NavLink>
            </div>
        </>
    );
}

export default Navigation;
